/**
 * Promise wrapper around the shared http client for ``POST /customer-intents``.
 *
 * Rejections are ``Error`` instances whose ``detail`` carries the backend's
 * human-readable message (same contract as the feedback API) so callers can
 * surface ``err.detail`` directly in a toast.
 */
import { data_from_server_get, data_from_server_post } from '@/shared/http/client';
import { CUSTOMER_INTENT_SEND_FAILED_MESSAGE } from '../constants';

export const CLOUD_DEPLOY_ENDPOINT = 'customer-intents/cloud-deploy';

const toError = (detail, fallback) => {
    const message = typeof detail === 'string' && detail.trim() ? detail : fallback;
    const err = new Error(message);
    err.detail = message;
    return err;
};

const toCloudDeployStatus = (result) => ({
    requested: result?.requested === true,
    requestedAt: result?.requested_at ?? null,
});

/**
 * ``GET /customer-intents/cloud-deploy`` → ``{requested, requestedAt}`` for the
 * signed-in user. Resolves ``{requested: false}`` when the endpoint is
 * unavailable (404 off SaaS / ClickUp not configured) or the call fails, so
 * the menu simply offers the option.
 */
export function getCloudDeployStatus() {
    return new Promise((resolve) => {
        data_from_server_get(CLOUD_DEPLOY_ENDPOINT, (result) => resolve(toCloudDeployStatus(result))).catch(
            () => resolve(toCloudDeployStatus(null)),
        );
    });
}

/**
 * ``POST /customer-intents/cloud-deploy`` ``{workspace}`` → ``{requested, requestedAt}``.
 * Idempotent server-side: a repeat call returns the stored request without
 * filing a second ClickUp task.
 */
export function requestCloudDeploy({ workspace } = {}) {
    return new Promise((resolve, reject) => {
        data_from_server_post(
            CLOUD_DEPLOY_ENDPOINT,
            { workspace: workspace ?? null },
            (result) => resolve(toCloudDeployStatus(result)),
            (detail) => reject(toError(detail, CUSTOMER_INTENT_SEND_FAILED_MESSAGE)),
        ).catch((e) => reject(toError(e?.detail || e?.message, CUSTOMER_INTENT_SEND_FAILED_MESSAGE)));
    });
}

/**
 * ``POST /customer-intents`` ``{features, autonomy_levels, something_else, workspace}``
 * → ``{task_id, url}``. User email and platform URL are added server-side.
 * ``autonomy_level`` (first level or null) is still sent for backends that
 * predate the multi-select tiles.
 */
export function createCustomerIntent({ features, autonomyLevels, somethingElse, workspace }) {
    const levels = autonomyLevels ?? [];
    return new Promise((resolve, reject) => {
        data_from_server_post(
            'customer-intents',
            {
                features: features ?? [],
                autonomy_levels: levels,
                autonomy_level: levels[0] ?? null,
                something_else: somethingElse ?? '',
                workspace: workspace ?? null,
            },
            resolve,
            (detail) => reject(toError(detail, CUSTOMER_INTENT_SEND_FAILED_MESSAGE)),
        ).catch((e) => reject(toError(e?.detail || e?.message, CUSTOMER_INTENT_SEND_FAILED_MESSAGE)));
    });
}
